import React, { useState, useEffect } from 'react';
import {
  BarChart3, Eye, MousePointer, TrendingUp, Building2, ArrowLeft, RefreshCw,
  Calendar, Download, FileText, Award, Star, CheckCircle, Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { bannerAds, analyticsDb } from '../lib/supabase';

// ==================== ADVERTISER PORTAL ====================
// Shows advertisers their own listing and ad statistics
export const AdvertiserPortal = ({ setPage }) => {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [listings, setListings] = useState([]);
  const [ads, setAds] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);

  const getDateRange = (period) => {
    const end = new Date();
    const start = new Date();
    switch (period) {
      case 'week': start.setDate(end.getDate() - 7); break;
      case 'month': start.setMonth(end.getMonth() - 1); break;
      case 'quarter': start.setMonth(end.getMonth() - 3); break;
      case 'year': start.setFullYear(end.getFullYear() - 1); break;
      default: start.setDate(end.getDate() - 30);
    }
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  };

  useEffect(() => {
    if (user) loadData();
  }, [user, period]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get user's ads
      const adsRes = await bannerAds.getByAdvertiser(user.id);
      setAds(adsRes.data || []);

      // In a real implementation, we'd fetch the user's listings here
      // For now, we'll show placeholder data
      setListings([]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');

    const doc = new jsPDF();
    const { start, end } = getDateRange(period);

    doc.setFontSize(20);
    doc.setTextColor(6, 95, 70);
    doc.text("Jamaica Driver's Guide", 14, 20);
    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text('Advertiser Performance Report', 14, 28);
    doc.setFontSize(10);
    doc.text(`Period: ${start} to ${end}`, 14, 35);
    doc.text(`Account: ${profile?.full_name || profile?.email}`, 14, 42);

    // Ads Performance
    if (ads.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Banner Ad Performance', 14, 55);
      doc.autoTable({
        startY: 60,
        head: [['Ad Name', 'Impressions', 'Clicks', 'CTR']],
        body: ads.map(ad => [
          ad.name,
          ad.impressions?.toLocaleString() || '0',
          ad.clicks?.toLocaleString() || '0',
          ((ad.ctr || 0) * 100).toFixed(2) + '%'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [6, 95, 70] }
      });
    }

    // Summary
    const totalImpressions = ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
    const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

    const summaryY = ads.length > 0 ? doc.lastAutoTable.finalY + 15 : 55;
    doc.text('Summary', 14, summaryY);
    doc.autoTable({
      startY: summaryY + 5,
      body: [
        ['Total Impressions', totalImpressions.toLocaleString()],
        ['Total Clicks', totalClicks.toLocaleString()],
        ['Average CTR', avgCtr.toFixed(2) + '%'],
        ['Active Ads', ads.filter(a => a.is_active).length.toString()]
      ],
      theme: 'plain'
    });

    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 285);

    doc.save(`performance_report_${period}.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const totalImpressions = ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
  const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <BarChart3 className="w-8 h-8" />My Analytics
            </h2>
            <p className="text-violet-200 mt-1">Track your listing and ad performance</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={period} onChange={(e) => setPeriod(e.target.value)}
              className="px-4 py-2 rounded-lg bg-white/20 text-white border-0">
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last 3 Months</option>
              <option value="year">Last Year</option>
            </select>
            <button onClick={loadData} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-lg border-l-4 border-blue-500">
          <Eye className="w-6 h-6 text-blue-500 mb-2" />
          <p className="text-2xl font-black">{totalImpressions.toLocaleString()}</p>
          <p className="text-gray-500 text-sm">Total Impressions</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-lg border-l-4 border-green-500">
          <MousePointer className="w-6 h-6 text-green-500 mb-2" />
          <p className="text-2xl font-black">{totalClicks.toLocaleString()}</p>
          <p className="text-gray-500 text-sm">Total Clicks</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-lg border-l-4 border-amber-500">
          <TrendingUp className="w-6 h-6 text-amber-500 mb-2" />
          <p className="text-2xl font-black">{avgCtr.toFixed(2)}%</p>
          <p className="text-gray-500 text-sm">Avg Click Rate</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-lg border-l-4 border-purple-500">
          <Award className="w-6 h-6 text-purple-500 mb-2" />
          <p className="text-2xl font-black">{ads.filter(a => a.is_active).length}</p>
          <p className="text-gray-500 text-sm">Active Ads</p>
        </div>
      </div>

      {/* Banner Ads Performance */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-amber-500 text-white p-4">
          <h3 className="font-bold flex items-center gap-2">
            <Building2 className="w-5 h-5" />Banner Ad Performance
          </h3>
        </div>
        <div className="p-4">
          {ads.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-500">No Ads Yet</h4>
              <p className="text-gray-400 mt-1">Your banner ad statistics will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Ad Name</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Impressions</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Clicks</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ads.map((ad) => (
                    <tr key={ad.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {ad.image_url && (
                            <img src={ad.image_url} alt="" className="w-16 h-8 object-contain bg-gray-100 rounded" />
                          )}
                          <div>
                            <p className="font-medium">{ad.name}</p>
                            <p className="text-xs text-gray-500">{(ad.pages || []).join(', ')}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${ad.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                          {ad.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{(ad.impressions || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-medium text-green-600">{(ad.clicks || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-medium text-amber-600">{((ad.ctr || 0) * 100).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Tier Info */}
      <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-2 border-emerald-200 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Star className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-emerald-900">Upgrade Your Listing</h4>
            <p className="text-emerald-700 mt-1">
              Get more visibility with a premium or sponsored listing. Premium listings appear at the top of search results and include detailed analytics.
            </p>
            <button onClick={() => setPage('getlisted')} className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700">
              Learn More About Listing Tiers
            </button>
          </div>
        </div>
      </div>

      {/* Export */}
      <div className="flex justify-end">
        <button onClick={exportReport} className="flex items-center gap-2 px-6 py-3 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700">
          <Download className="w-5 h-5" />Export PDF Report
        </button>
      </div>

      <button onClick={() => setPage('home')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Home
      </button>
    </div>
  );
};

export default AdvertiserPortal;
